import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { JobCardActivityDrawer } from "./JobCardActivityDrawer";
import { VehicleServiceHistoryDrawer } from "./VehicleServiceHistoryDrawer";
import {
  CustomerServiceHistoryDrawer,
  CustomerHistoryVehicleTarget,
} from "./CustomerServiceHistoryDrawer";
import { HistoryClockIcon } from "./HistoryIcons";

export type InspectionTimelineTab = "jobcard" | "vehicle" | "customer";

export interface InspectionTimelineDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  jobcardId: string | null;
  vehicleNo?: string | null;
  vinNo?: string | null;
  customerId?: string | null;
  rowSnapshot?: Record<string, any> | null;
  enableJobcardTab?: boolean;
  enableVehicleTab?: boolean;
  enableCustomerTab?: boolean;
}

type VehicleFocus = {
  vehicleNo: string | null;
  vinNo: string | null;
};

function resolveDefaultTab(
  jobcardId: string | null,
  hasVehicleKey: boolean,
  hasCustomerKey: boolean,
  enableJobcardTab: boolean,
  enableVehicleTab: boolean,
  enableCustomerTab: boolean
): InspectionTimelineTab {
  if (enableJobcardTab && jobcardId) return "jobcard";
  if (enableVehicleTab && hasVehicleKey) return "vehicle";
  if (enableCustomerTab && hasCustomerKey) return "customer";
  if (enableJobcardTab) return "jobcard";
  if (enableVehicleTab) return "vehicle";
  return "customer";
}

function displayOrDash(value: unknown): string {
  const s = value == null ? "" : String(value).trim();
  return s || "—";
}

export const InspectionTimelineDrawer: React.FC<InspectionTimelineDrawerProps> = ({
  isOpen,
  onClose,
  jobcardId,
  vehicleNo,
  vinNo,
  customerId,
  rowSnapshot,
  enableJobcardTab = true,
  enableVehicleTab = true,
  enableCustomerTab = true,
}) => {
  const [vehicleFocus, setVehicleFocus] = useState<VehicleFocus | null>(null);

  const effectiveVehicleNo = vehicleFocus?.vehicleNo ?? vehicleNo ?? null;
  const effectiveVinNo = vehicleFocus?.vinNo ?? vinNo ?? null;

  const propHasVehicleKey = Boolean(
    (vehicleNo && String(vehicleNo).trim()) || (vinNo && String(vinNo).trim())
  );
  const hasVehicleKey = Boolean(
    (effectiveVehicleNo && String(effectiveVehicleNo).trim()) ||
      (effectiveVinNo && String(effectiveVinNo).trim())
  );
  const hasCustomerKey = Boolean(
    (customerId && String(customerId).trim()) || propHasVehicleKey
  );
  const showJobcardTab = enableJobcardTab;
  const showVehicleTab = enableVehicleTab;
  const showCustomerTab = enableCustomerTab;

  const defaultTab = useMemo(
    () =>
      resolveDefaultTab(
        jobcardId,
        propHasVehicleKey,
        hasCustomerKey,
        showJobcardTab,
        showVehicleTab,
        showCustomerTab
      ),
    [
      jobcardId,
      propHasVehicleKey,
      hasCustomerKey,
      showJobcardTab,
      showVehicleTab,
      showCustomerTab,
    ]
  );

  const [activeTab, setActiveTab] = useState<InspectionTimelineTab>(defaultTab);

  useEffect(() => {
    if (!isOpen) {
      setVehicleFocus(null);
      return;
    }
    setVehicleFocus(null);
    setActiveTab(
      resolveDefaultTab(
        jobcardId,
        propHasVehicleKey,
        hasCustomerKey,
        showJobcardTab,
        showVehicleTab,
        showCustomerTab
      )
    );
  }, [
    isOpen,
    jobcardId,
    vehicleNo,
    vinNo,
    customerId,
    propHasVehicleKey,
    hasCustomerKey,
    showJobcardTab,
    showVehicleTab,
    showCustomerTab,
  ]);

  const openVehicleTimeline = useCallback(
    (target: CustomerHistoryVehicleTarget) => {
      if (!showVehicleTab) return;
      setVehicleFocus({
        vehicleNo: target.vehicleNo ? String(target.vehicleNo).trim() : null,
        vinNo: target.vinNo ? String(target.vinNo).trim() : null,
      });
      setActiveTab("vehicle");
    },
    [showVehicleTab]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const jobcardLabel = displayOrDash(
    rowSnapshot?.jobcard_no || rowSnapshot?.jobcardNo || jobcardId
  );
  const customerLabel = displayOrDash(
    rowSnapshot?.customername ||
      rowSnapshot?.customer_name ||
      rowSnapshot?.customerName
  );
  const vehicleLabel = displayOrDash(
    effectiveVehicleNo ||
      rowSnapshot?.reg_no ||
      rowSnapshot?.platenumber ||
      rowSnapshot?.plate_number ||
      effectiveVinNo
  );

  const jobcardDisabled = showJobcardTab && !jobcardId;
  const vehicleDisabled = showVehicleTab && !hasVehicleKey;
  const customerDisabled = showCustomerTab && !hasCustomerKey;
  const showHeaderLinks = showJobcardTab || showVehicleTab || showCustomerTab;

  const linkClass = (tab: InspectionTimelineTab, disabled: boolean) => {
    const isActive = activeTab === tab;
    const base =
      "inline-flex flex-1 min-w-0 items-center justify-center truncate rounded-full border px-2.5 py-1 text-xs font-medium transition-colors";
    if (disabled) {
      return `${base} border-white/15 bg-white/5 text-white/40 cursor-not-allowed`;
    }
    if (isActive) {
      return `${base} border-white/40 bg-white text-primary-800 shadow-sm`;
    }
    return `${base} border-white/25 bg-white/15 text-white hover:bg-white/25 cursor-pointer`;
  };

  const body = (
    <>
      <div
        className="fixed inset-0 z-[11999] bg-black/50 backdrop-blur-sm"
        aria-hidden
        onClick={onClose}
      />
      <div
        className="fixed top-0 right-0 z-[12000] h-full w-full max-w-[560px] bg-slate-50 dark:bg-gray-900 shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-700"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inspection-timeline-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 py-5 text-white bg-gradient-to-br from-primary-800 to-primary-500">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2
                id="inspection-timeline-drawer-title"
                className="text-base font-bold m-0 flex items-center gap-2"
              >
                <HistoryClockIcon className="w-4 h-4 opacity-90 shrink-0" />
                Service Timeline
              </h2>
              {showHeaderLinks ? (
                <div
                  className="mt-3 flex w-full items-center justify-between gap-2 text-xs"
                  role="tablist"
                  aria-label="Timeline context"
                >
                  {showJobcardTab ? (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeTab === "jobcard"}
                      aria-label={`Job Card ${jobcardLabel}`}
                      disabled={jobcardDisabled}
                      title={
                        jobcardDisabled
                          ? "No job card linked to this inspection yet"
                          : "Show job card timeline"
                      }
                      onClick={() => !jobcardDisabled && setActiveTab("jobcard")}
                      className={linkClass("jobcard", jobcardDisabled)}
                    >
                      {jobcardLabel}
                    </button>
                  ) : null}
                  {showCustomerTab ? (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeTab === "customer"}
                      aria-label={`Customer ${customerLabel}`}
                      disabled={customerDisabled}
                      title={
                        customerDisabled
                          ? "No customer available"
                          : "Show customer service history"
                      }
                      onClick={() =>
                        !customerDisabled && setActiveTab("customer")
                      }
                      className={linkClass("customer", customerDisabled)}
                    >
                      {customerLabel}
                    </button>
                  ) : null}
                  {showVehicleTab ? (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeTab === "vehicle"}
                      aria-label={`Vehicle ${vehicleLabel}`}
                      disabled={vehicleDisabled}
                      title={
                        vehicleDisabled
                          ? "No vehicle number available"
                          : "Show vehicle service history"
                      }
                      onClick={() => !vehicleDisabled && setActiveTab("vehicle")}
                      className={linkClass("vehicle", vehicleDisabled)}
                    >
                      {vehicleLabel}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white/90 hover:text-white text-2xl leading-none px-1 shrink-0"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden relative">
          {/* Keep panels mounted so tab state stays independent */}
          {showJobcardTab ? (
            <div
              className={`absolute inset-0 ${
                activeTab === "jobcard" ? "block" : "hidden"
              }`}
              role="tabpanel"
              hidden={activeTab !== "jobcard"}
            >
              <JobCardActivityDrawer
                variant="embedded"
                isOpen={isOpen}
                isActive={activeTab === "jobcard"}
                onClose={onClose}
                jobcardId={jobcardId}
                rowSnapshot={rowSnapshot}
              />
            </div>
          ) : null}
          {showVehicleTab ? (
            <div
              className={`absolute inset-0 ${
                activeTab === "vehicle" ? "block" : "hidden"
              }`}
              role="tabpanel"
              hidden={activeTab !== "vehicle"}
            >
              <VehicleServiceHistoryDrawer
                variant="embedded"
                isOpen={isOpen}
                isActive={activeTab === "vehicle"}
                onClose={onClose}
                vehicleNo={effectiveVehicleNo}
                vinNo={effectiveVinNo}
                rowSnapshot={
                  vehicleFocus
                    ? {
                        ...(rowSnapshot || {}),
                        platenumber:
                          effectiveVehicleNo ||
                          rowSnapshot?.platenumber ||
                          rowSnapshot?.plate_number,
                        plate_number:
                          effectiveVehicleNo ||
                          rowSnapshot?.plate_number ||
                          rowSnapshot?.platenumber,
                        vin_no:
                          effectiveVinNo ||
                          rowSnapshot?.vin_no ||
                          rowSnapshot?.vin_number,
                      }
                    : rowSnapshot
                }
              />
            </div>
          ) : null}
          {showCustomerTab ? (
            <div
              className={`absolute inset-0 ${
                activeTab === "customer" ? "block" : "hidden"
              }`}
              role="tabpanel"
              hidden={activeTab !== "customer"}
            >
              <CustomerServiceHistoryDrawer
                variant="embedded"
                isOpen={isOpen}
                isActive={activeTab === "customer"}
                onClose={onClose}
                customerId={customerId}
                vehicleNo={vehicleNo}
                vinNo={vinNo}
                rowSnapshot={rowSnapshot}
                enableVehicleTimelineLink={showVehicleTab}
                onOpenVehicleTimeline={
                  showVehicleTab ? openVehicleTimeline : undefined
                }
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(body, document.body);
};

export default InspectionTimelineDrawer;
